export type ObjectiveLevel = '1st' | '2nd' | '3rd' | '4th'

export const QUARTER_NUMBERS: Record<ObjectiveLevel, string> = { '1st': '1', '2nd': '2', '3rd': '3', '4th': '4' }

interface LevelStyles {
  actionButton: string
  solidButton: string
  actionText: string
  solidBackground: string
  lightBackground: string
  lightBackgroundText: string
  border: string
  tab: string
  sectionHeading: string
}

// The 4th quarter has no colour of its own, so it renders with these neutral defaults.
const UNCOLOURED_LEVEL_STYLES: LevelStyles = {
  actionButton: 'border-transparent text-gray-900',
  solidButton: '',
  actionText: 'text-white',
  solidBackground: '',
  lightBackground: '',
  lightBackgroundText: '',
  border: '',
  tab: 'text-blue-400 hover:bg-gray-200 data-[state=active]:text-gray-900',
  sectionHeading: ''
}

const STYLES_BY_LEVEL: Record<ObjectiveLevel, LevelStyles> = {
  '1st': {
    actionButton: 'border-objective-tealAction bg-objective-tealAction text-white hover:border-objective-tealActionHover hover:bg-objective-tealActionHover',
    solidButton: 'bg-objective-teal hover:bg-objective-tealHover',
    actionText: 'text-objective-tealAction',
    solidBackground: 'bg-objective-teal',
    lightBackground: 'bg-objective-tealLight',
    lightBackgroundText: '',
    border: 'border-objective-teal',
    tab: 'bg-objective-tealTab text-white hover:bg-objective-teal data-[state=active]:bg-objective-teal',
    sectionHeading: 'border-objective-teal text-objective-teal'
  },
  '2nd': {
    actionButton: 'border-objective-blueAction bg-objective-blueAction text-white hover:border-objective-blueActionHover hover:bg-objective-blueActionHover',
    solidButton: 'bg-objective-blue hover:bg-objective-blueHover',
    actionText: 'text-objective-blueAction',
    solidBackground: 'bg-objective-blue',
    lightBackground: 'bg-objective-blueLight',
    lightBackgroundText: '',
    border: 'border-objective-blue',
    tab: 'bg-objective-blueTab text-white hover:bg-objective-blue data-[state=active]:bg-objective-blue',
    sectionHeading: 'border-objective-blue text-objective-blue'
  },
  '3rd': {
    actionButton: 'border-objective-purpleAction bg-objective-purpleAction text-white hover:border-objective-purpleActionHover hover:bg-objective-purpleActionHover',
    solidButton: 'bg-objective-purple hover:bg-objective-purpleHover',
    actionText: 'text-objective-purpleAction',
    solidBackground: 'bg-objective-purple',
    lightBackground: 'bg-objective-purpleLight',
    // Vue 2 kept the dark body text here, at 2.7:1; white meets AA on this darker "light" purple.
    lightBackgroundText: 'text-white',
    border: 'border-objective-purple',
    tab: 'bg-objective-purpleLight text-white hover:bg-objective-purple data-[state=active]:bg-objective-purple',
    sectionHeading: 'border-objective-purple text-objective-purple'
  },
  '4th': UNCOLOURED_LEVEL_STYLES
}

const BUTTON_BASE_CLASSES =
  'inline-flex items-center gap-1 rounded-xs border px-3 py-1.5 text-text-md leading-5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-900'

export const DEFAULT_BUTTON_CLASSES = `${BUTTON_BASE_CLASSES} border-gray-400 bg-white text-gray-900 hover:border-gray-500 hover:bg-gray-300`

export function levelButtonClasses(level: ObjectiveLevel): string {
  return `${BUTTON_BASE_CLASSES} ${STYLES_BY_LEVEL[level].actionButton}`
}

export function levelSolidButtonClass(level: ObjectiveLevel): string {
  return STYLES_BY_LEVEL[level].solidButton
}

export function levelActionTextClass(level: ObjectiveLevel): string {
  return STYLES_BY_LEVEL[level].actionText
}

export function levelSolidBackgroundClass(level: ObjectiveLevel): string {
  return STYLES_BY_LEVEL[level].solidBackground
}

export function levelLightBackgroundClass(level: ObjectiveLevel): string {
  return STYLES_BY_LEVEL[level].lightBackground
}

export function levelLightBackgroundTextClass(level: ObjectiveLevel): string {
  return STYLES_BY_LEVEL[level].lightBackgroundText
}

export function levelBorderClass(level: ObjectiveLevel): string {
  return STYLES_BY_LEVEL[level].border
}

export function levelTabClass(level: ObjectiveLevel): string {
  return STYLES_BY_LEVEL[level].tab
}

export function levelSectionHeadingClass(level: ObjectiveLevel): string {
  return STYLES_BY_LEVEL[level].sectionHeading
}
